import {Component, Input, OnInit, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';

import {ComplianceDocumentType} from '../../../constants/compliance-document-type';
import {AbstractDocumentsModel} from '../../../models/abstract-documents-model';
import {ComplianceUploadFile} from '../../../models/compliance-upload-file';
import {UploadFile} from '../../../models/upload-file';
import {UploadFileService} from '../../../services/upload-file/upload-file.service';
import {PartnerComplianceDocumentService} from '../../../services/partner/partner-compliance-document.service';
import {AdvertiserComplianceDocumentService} from '../../../services/advertiser/advertiser-compliance-document.service';
import {AbstractModelService} from '../../../services/model/abstract-model.service';
import {Partner} from '../../../models/partner/partner';
import {ComplianceDocument} from '../../../models/compliance-document';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'app-compliance-documents',
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe
],
  templateUrl: './compliance-documents.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './compliance-documents.component.scss',
})
export class ComplianceDocumentsComponent implements OnInit {

  @Input() model!: AbstractDocumentsModel;

  documentTypeList: ComplianceDocumentType[] = [
    ComplianceDocumentType.PROOF_OF_IDENTITY,
    ComplianceDocumentType.CERTIFICATE_OF_INCORPORATION,
    ComplianceDocumentType.SIGNED_CONTRACT,
    ComplianceDocumentType.INSERTION_ORDER,
  ];

  upload: Record<ComplianceDocumentType, ComplianceUploadFile> = {
    [ComplianceDocumentType.PROOF_OF_IDENTITY]: new ComplianceUploadFile(),
    [ComplianceDocumentType.CERTIFICATE_OF_INCORPORATION]: new ComplianceUploadFile(),
    [ComplianceDocumentType.SIGNED_CONTRACT]: new ComplianceUploadFile(),
    [ComplianceDocumentType.INSERTION_ORDER]: new ComplianceUploadFile(),
  };

  constructor(
    private partnerComplianceDocumentService: PartnerComplianceDocumentService,
    private advertiserComplianceDocumentService: AdvertiserComplianceDocumentService,
    private uploadFileService: UploadFileService
  ) {
  }

  ngOnInit(): void {
    for (const [type, document] of Object.entries(this.model.documents ?? [])) {
      this.upload[type as ComplianceDocumentType] = new ComplianceUploadFile().deserialize({
        id: document.id ?? null,
        expireAtDate: document?.getExpireAtDate() ?? null,
        status: document?.status ?? 'pending'
      });
    }
  }

  onClickUpload(event: Event, complianceDocumentType: ComplianceDocumentType): void {
    const target: any = event.target;

    this.uploadDocument(target?.files[0], complianceDocumentType);
  }

  sendUploadedFile(index: number = 0): void {
    const type = this.documentTypeList[index];
    if (!type) {
      // We reached the end of the list
      return;
    }

    const complianceUpload: ComplianceUploadFile = this.upload[type];
    if (complianceUpload.isEmpty()) {
      return this.sendUploadedFile(index + 1);
    }

    const data = complianceUpload.getData(type)

    if (complianceUpload.id) {
      this.getService().update(complianceUpload.id, data).subscribe(() => {
        this.sendUploadedFile(index + 1);
      });
    } else {
      this.getService().create(this.getCreateData(data)).subscribe(() => {
        this.sendUploadedFile(index + 1);
      });
    }
  }

  private getCreateData(createData: any): any {
    if (this.isPartner()) {
      createData.partnerId = this.model.id;
    } else {
      createData.advertiserId = this.model.id;
    }

    return createData;
  }

  private uploadDocument(file: File, complianceDocumentType: ComplianceDocumentType): void {
    this.uploadFileService.upload(file).subscribe((file: UploadFile) => {
      this.upload[complianceDocumentType].document = file;
    });
  }

  private isPartner(): boolean {
    return this.model instanceof Partner;
  }

  private getService(): AbstractModelService<ComplianceDocument> {
    return this.isPartner() ? this.partnerComplianceDocumentService : this.advertiserComplianceDocumentService;
  }
}
